// Çevrimdışı "Bugün Sahada": cihazdaki onaylı taleplerden bugüne denk gelenler, Lokasyon → Firma → Personel
import { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { HardHat, MapPin, Building2, Search, Truck, User, Clock } from 'lucide-react';
import { phaseOf, PHASE } from './TodayOnSite';
import { iso } from '../settings/calendarViews';
import { maskTcNo } from './constants';

const norm = (s) => (s || '').toLocaleLowerCase('tr');
const personHit = (e, t) => !t || norm(`${e.name} ${e.surname}`).includes(t) || (e.tc_kimlik_no || '').includes(t);

const Job = ({ it, phase, t, onView }) => {
  const people = it.employees.filter(e => personHit(e, t));
  return (
    <div className={`rounded-lg border bg-white p-3 ${phase === 'done' ? 'opacity-60' : ''}`} data-testid={`offline-today-job-${it.permit.id}`}>
      <button type="button" onClick={() => onView(it)} className="flex w-full flex-wrap items-center gap-2 text-left text-sm">
        <span className="flex items-center gap-1 font-semibold text-slate-900"><Clock className="h-3.5 w-3.5" />{it.permit.start_time || '-'}–{it.permit.end_time || '-'}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${PHASE[phase][1]}`} data-testid={`offline-today-phase-${it.permit.id}`}>{PHASE[phase][0]}</span>
        <span className="font-mono text-xs text-slate-500">#{it.permit.request_number || '-'}</span>
        <span className="min-w-0 flex-1 truncate text-slate-600">{it.permit.job_description}</span>
        <span className="text-xs text-slate-400">Detay ›</span>
      </button>
      <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
        {people.map(e => (
          <div key={e.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-sm" data-testid={`offline-today-person-${it.permit.id}-${e.id}`}>
            <User className="h-3.5 w-3.5 text-slate-400" /><span className="font-medium text-slate-800">{e.name} {e.surname}</span>
            <span className="ml-auto font-mono text-xs text-slate-500">{maskTcNo(e.tc_kimlik_no)}</span>
          </div>
        ))}
      </div>
      {it.equipment.length > 0 && <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><Truck className="h-3.5 w-3.5" />{it.equipment.map(q => q.name).join(', ')}</p>}
    </div>
  );
};

const group = (rows, key) => rows.reduce((acc, x) => ({ ...acc, [key(x)]: [...(acc[key(x)] || []), x] }), {});

export const OfflineTodayOnSite = ({ items, onView }) => {
  const [tick, setTick] = useState(Date.now());
  const [q, setQ] = useState('');
  useEffect(() => { const i = setInterval(() => setTick(Date.now()), 60 * 1000); return () => clearInterval(i); }, []);
  const d = new Date(tick);
  const today = iso(d);
  const now = d.getHours() * 60 + d.getMinutes();
  const t = norm(q.trim());
  const rows = items
    .filter(it => (it.permit.start_date || '').slice(0, 10) <= today && (it.permit.end_date || '').slice(0, 10) >= today)
    .map(it => ({ it, phase: phaseOf({ ...it.permit, start_date: it.permit.start_date?.slice(0, 10), end_date: it.permit.end_date?.slice(0, 10) }, today, now) }));
  const shown = rows.filter(x => !t || x.it.employees.some(e => personHit(e, t)));
  const people = shown.reduce((n, x) => n + x.it.employees.filter(e => personHit(e, t)).length, 0);
  const byLoc = group(shown, x => x.it.location_name);
  return (
    <div className="space-y-4" data-testid="offline-today-onsite">
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Personel adı veya TC ile ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="offline-today-search" />
        </div>
        <span className="flex items-center gap-1.5 text-sm text-emerald-800" data-testid="offline-today-summary"><HardHat className="h-4 w-4" />{shown.length} iş · {Object.keys(byLoc).length} lokasyon · {people} personel · {shown.filter(x => x.phase === 'run').length} devam ediyor</span>
      </div>
      {shown.length === 0
        ? <p className="rounded-xl border border-dashed p-10 text-center text-slate-500" data-testid="offline-today-empty">{t ? 'Aramaya uygun personel bugün sahada değil.' : 'Bugün sahada olması gereken onaylı iş bulunmuyor.'}</p>
        : Object.entries(byLoc).sort(([a], [b]) => a.localeCompare(b, 'tr')).map(([loc, list]) => (
          <div key={loc} className="overflow-hidden rounded-xl border border-emerald-200" data-testid={`offline-today-loc-${loc}`}>
            <p className="flex items-center gap-1.5 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-900"><MapPin className="h-4 w-4" />{loc} <span className="font-normal">({list.length} iş)</span></p>
            {Object.entries(group(list, x => x.it.company_name)).sort(([a], [b]) => a.localeCompare(b, 'tr')).map(([firm, jobs]) => (
              <div key={firm} className="space-y-2 border-t bg-slate-50/60 px-4 py-3">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Building2 className="h-4 w-4 text-purple-600" />{firm}</p>
                {jobs.sort((a, b) => (a.it.permit.start_time || '').localeCompare(b.it.permit.start_time || '')).map(x => <Job key={x.it.permit.id} it={x.it} phase={x.phase} t={t} onView={onView} />)}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};
